import type { ReactNode } from 'react';

import { WhitePanel } from 'components/Toolkit/WhitePanel/WhitePanel';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { MiniUserProfile } from 'components/Toolkit/MiniUserProfile/MiniUserProfile';

import { VerifiedList } from 'components/PageWrappers/AdDetails/components/VerifiedList/VerifiedList';
import { Verifications } from 'components/PageWrappers/AdDetails/components/Verifications/Verifications';
import type {
  UserRating,
  UserVerification,
} from 'components/PageWrappers/AdDetails/types';
import { StarRatingGroup } from 'components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';

import * as Styled from 'components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel.styled';
import { VerifiedTitle } from 'components/PageWrappers/AdDetails/components/Verifications/VerifiedTitle/VerifiedTitle';
import { FEATURE_FEEDBACK } from 'utils';
import { PrivateSellerRatings } from 'components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel.styled';
import { Spacer } from 'helpers/genericStyles';

export interface SideSellerInfoPanelProps {
  title?: string;
  renderSendMessage?: ReactNode;
  renderShowPhone?: ReactNode;
  renderMakeOffer?: ReactNode;
  county?: string;
  type?: string;
  sellerUrl?: string;
  isTrader: boolean;
  verification?: UserVerification;
  className?: string;
  testId?: string;
  isDealer?: boolean;
  showroomUrl?: string;
  illustration?: string;
  dealerReviews?: string;
  dealerRating?: string;
  userRating?: UserRating;
  membershipDuration?: string;
  onClickRatings?: () => void;
  onClickReviews?: () => void;
}

function SideSellerInfoPanel({
  title,
  renderSendMessage,
  renderShowPhone,
  renderMakeOffer,
  county,
  type,
  sellerUrl,
  isTrader,
  verification,
  className,
  testId,
  isDealer,
  showroomUrl,
  illustration,
  dealerReviews,
  dealerRating,
  userRating,
  membershipDuration,
  onClickRatings,
  onClickReviews,
}: SideSellerInfoPanelProps) {
  const DISPLAY_USER_RATING = !isDealer && FEATURE_FEEDBACK;

  const renderVerificationComponent = (verification?: UserVerification) => {
    if (!verification || isDealer) return null;

    if (DISPLAY_USER_RATING) {
      return <Verifications verifications={verification} />;
    }

    return <VerifiedList verification={verification} />;
  };

  return (
    <WhitePanel className={className} testId={testId}>
      <Styled.MiniUserProfileAndVerifiedListContainer
        googleReviews={dealerReviews}
      >
        {title && (
          <MiniUserProfile
            title={
              DISPLAY_USER_RATING && verification ? (
                <VerifiedTitle title={title} verifications={verification} />
              ) : (
                title
              )
            }
            illustration={illustration}
            county={county}
            type={type}
            isTrader={isTrader}
            verification={verification}
            isDealer={isDealer}
            position="SIDE"
          />
        )}

        <Spacer marginBottom="M16" />

        {renderVerificationComponent(verification)}
      </Styled.MiniUserProfileAndVerifiedListContainer>
      {dealerReviews && (
        <Styled.DealerRatings>
          {dealerRating && (
            <Styled.DealerReviewInfoTitle
              title={''}
              listItems={[
                <StarRatingGroup
                  key={1}
                  rating={parseFloat(dealerRating)}
                  totalReviews={dealerReviews}
                  clickTargetElementId="dealer-panel"
                  starWidth="20"
                  starHeight="20"
                  totalReviewsTrackingId={'google-reviews-total-reviews'}
                />,
              ]}
            />
          )}
        </Styled.DealerRatings>
      )}

      {DISPLAY_USER_RATING && onClickReviews && onClickRatings && (
        <PrivateSellerRatings
          membershipDuration={membershipDuration}
          userRating={userRating}
          onClickRatings={onClickRatings}
          onClickReviews={onClickReviews}
        />
      )}

      <Styled.ButtonList>
        {renderMakeOffer && (
          <Styled.ButtonItem>{renderMakeOffer}</Styled.ButtonItem>
        )}
        {renderSendMessage && (
          <Styled.ButtonItem>{renderSendMessage}</Styled.ButtonItem>
        )}
        {renderShowPhone && (
          <Styled.ButtonItem>{renderShowPhone}</Styled.ButtonItem>
        )}

        {sellerUrl && (
          <Styled.ButtonItem>
            <ButtonLink
              href={showroomUrl || sellerUrl}
              ofType="SECONDARY"
              data-testid="view-all-ads"
              data-tracking="view-all-ads-button"
              rel={isDealer ? undefined : 'nofollow'}
            >
              {isDealer
                ? 'Visit Showroom'
                : isTrader
                ? 'View Our Shop'
                : 'View All Ads'}
            </ButtonLink>
          </Styled.ButtonItem>
        )}
      </Styled.ButtonList>
    </WhitePanel>
  );
}

export { SideSellerInfoPanel };
